import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Sparkles, LogOut, Menu, X, LogIn, UserPlus, BookOpen, KeyRound } from "lucide-react";
import ChangePasswordModal from "@/components/ChangePasswordModal";

const Navbar = () => {
  const { user, logout } = useAuth();
  const { lang, toggle, t } = useLang();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const isEn = lang === "en";
  const guideLabel = isEn ? "How It Works" : "Come Funziona";
  const guidePath = isEn ? "/guide" : "/guida";
  const primaDopoLabel = isEn ? "Before & After" : "Prima e Dopo";

  const handleLogout = () => {
    logout();
    navigate("/");
    setMobileMenuOpen(false);
  };

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <header
        className="sticky top-0 z-50 w-full bg-[#0B1437] border-b border-[#D4AF37]/20 shadow-lg"
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between relative z-10">
          <Link to="/" onClick={closeMenu} className="flex items-center gap-2 group" data-testid="brand-link">
            <span className="w-7 h-7 rounded-full flex items-center justify-center bg-gradient-to-br from-[#F1D26B] via-[#D4AF37] to-[#7A5A18] shadow-[0_0_18px_rgba(212,175,55,0.35)]">
              <Sparkles className="w-3.5 h-3.5 text-[#0B1437]" strokeWidth={2} />
            </span>
            <span className="font-heading text-xl tracking-tight text-[#EDE7D8]">
              {t.brand}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-3 lg:gap-4">
            <button
              data-testid="lang-toggle"
              onClick={toggle}
              className="text-xs tracking-[0.28em] uppercase font-semibold text-[#B79A5A] hover:text-[#D4AF37] transition-colors duration-300 px-2 cursor-pointer"
            >
              {lang === "it" ? "IT / en" : "it / EN"}
            </button>

            {/* 1. Link Slide Prima e Dopo (Il gancio d'impatto) */}
            <Link
              to="/prima-e-dopo"
              data-testid="nav-prima-e-dopo"
              className="text-sm text-[#EDE7D8] hover:text-[#D4AF37] transition-colors duration-300 px-2 flex items-center gap-1.5"
            >
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span>{primaDopoLabel}</span>
            </Link>

            {/* 2. Link Guida / Come Funziona (L'approfondimento) */}
            <Link
              to={guidePath}
              data-testid="nav-guide"
              className="text-sm text-[#EDE7D8] hover:text-[#D4AF37] transition-colors duration-300 px-2 flex items-center gap-1.5"
            >
              <BookOpen className="w-4 h-4 text-[#D4AF37]" />
              <span>{guideLabel}</span>
            </Link>

            {user && typeof user === "object" ? (
              <>
                <Link
                  to="/dashboard"
                  data-testid="nav-trips"
                  className="text-sm text-[#EDE7D8] hover:text-[#D4AF37] transition-colors duration-300 px-2"
                >
                  {t.nav.trips}
                </Link>
                <Link
                  to="/wishes"
                  data-testid="nav-wishes"
                  className="text-sm text-[#EDE7D8] hover:text-[#D4AF37] transition-colors duration-300 px-2"
                >
                  {t.nav.wishes}
                </Link>
                <Link
                  to="/feedback"
                  data-testid="nav-feedback"
                  className="text-sm text-[#D4AF37] hover:text-[#F1D26B] transition-colors duration-300 px-2 font-semibold"
                >
                  {t.nav.feedback}
                </Link>
                <Link
                  to="/plan"
                  data-testid="nav-plan"
                  className="btn-pill text-sm"
                >
                  {t.nav.plan}
                </Link>

                {/* Tasto Cambio Password Utente Loggato */}
                <button
                  type="button"
                  onClick={() => setPasswordModalOpen(true)}
                  className="p-2 text-[#EDE7D8] hover:text-[#D4AF37] transition-colors duration-300 flex items-center gap-1 text-sm font-medium cursor-pointer"
                  title={isEn ? "Change Password" : "Cambia Password"}
                >
                  <KeyRound className="w-4 h-4 text-[#D4AF37]" />
                  <span className="hidden lg:inline">{isEn ? "Password" : "Password"}</span>
                </button>

                {/* Tasto Logout */}
                <button
                  data-testid="nav-logout"
                  onClick={handleLogout}
                  className="p-2 text-[#B79A5A] hover:text-[#D4AF37] transition-colors duration-300 flex items-center gap-1 text-sm font-medium cursor-pointer"
                  aria-label="logout"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden lg:inline">Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/feedback"
                  data-testid="nav-feedback"
                  className="text-sm text-[#D4AF37] hover:text-[#F1D26B] transition-colors duration-300 px-2 font-semibold"
                >
                  {t.nav.feedback}
                </Link>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="text-sm text-[#EDE7D8] hover:text-[#D4AF37] transition-colors duration-300 px-2"
                >
                  {t.nav.login}
                </Link>
                <Link to="/register" data-testid="nav-register" className="btn-pill text-sm">
                  {t.nav.register}
                </Link>
              </>
            )}
          </nav>

          {/* Mobile Hamburger & Lang Toggle */}
          <div className="flex md:hidden items-center gap-2">
            <button
              data-testid="lang-toggle-mobile"
              onClick={toggle}
              className="text-xs tracking-[0.2em] uppercase font-semibold text-[#B79A5A] px-2 py-1"
            >
              {lang === "it" ? "IT / en" : "it / EN"}
            </button>
            
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-[#EDE7D8] hover:text-[#D4AF37] transition-colors"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-x-0 top-16 bottom-0 bg-[#0B1437] z-50 py-8 px-6 flex flex-col space-y-5 overflow-y-auto border-t border-[#D4AF37]/20 shadow-2xl">
            
            {/* 1. Prima e Dopo nel Mobile */}
            <Link
              to="/prima-e-dopo"
              onClick={closeMenu}
              className="text-lg text-[#EDE7D8] hover:text-[#D4AF37] py-2 border-b border-gray-800 font-medium flex items-center gap-2"
            >
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <span>{primaDopoLabel}</span>
            </Link>

            {/* 2. Come Funziona nel Mobile */}
            <Link
              to={guidePath}
              onClick={closeMenu}
              className="text-lg text-[#EDE7D8] hover:text-[#D4AF37] py-2 border-b border-gray-800 font-medium flex items-center gap-2"
            >
              <BookOpen className="w-5 h-5 text-[#D4AF37]" />
              <span>{guideLabel}</span>
            </Link>

            {user && typeof user === "object" ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={closeMenu}
                  className="text-lg text-[#EDE7D8] hover:text-[#D4AF37] py-2 border-b border-gray-800 font-medium"
                >
                  {t.nav.trips}
                </Link>
                <Link
                  to="/wishes"
                  onClick={closeMenu}
                  className="text-lg text-[#EDE7D8] hover:text-[#D4AF37] py-2 border-b border-gray-800 font-medium"
                >
                  {t.nav.wishes}
                </Link>
                <Link
                  to="/feedback"
                  onClick={closeMenu}
                  className="text-lg text-[#D4AF37] hover:text-[#F1D26B] py-2 border-b border-gray-800 font-semibold"
                >
                  {t.nav.feedback}
                </Link>
                <Link
                  to="/plan"
                  onClick={closeMenu}
                  className="btn-pill text-base text-center py-3.5 mt-2 shadow-lg"
                >
                  {t.nav.plan}
                </Link>

                {/* Cambio Password Mobile */}
                <button
                  onClick={() => {
                    closeMenu();
                    setPasswordModalOpen(true);
                  }}
                  className="flex items-center justify-center gap-2 text-white bg-[#12204f] border border-gray-700 py-3 text-base font-medium rounded-xl mt-3"
                >
                  <KeyRound className="w-4 h-4 text-[#D4AF37]" />
                  <span>{isEn ? "Change Password" : "Cambia Password"}</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center gap-2 text-red-400 hover:text-red-300 py-3 text-base font-medium mt-1 bg-red-950/40 rounded-xl border border-red-900/40"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/feedback"
                  onClick={closeMenu}
                  className="text-lg text-[#D4AF37] hover:text-[#F1D26B] py-2 border-b border-gray-800 font-semibold"
                >
                  {t.nav.feedback}
                </Link>
                <Link
                  to="/login"
                  onClick={closeMenu}
                  className="flex items-center justify-center gap-2 text-base text-[#EDE7D8] bg-[#1b2559] py-3.5 rounded-xl border border-gray-700 font-medium"
                >
                  <LogIn className="w-5 h-5 text-[#D4AF37]" />
                  <span>{t.nav.login}</span>
                </Link>
                <Link
                  to="/register"
                  onClick={closeMenu}
                  className="flex items-center justify-center gap-2 btn-pill text-base text-center py-3.5 mt-2 shadow-lg"
                >
                  <UserPlus className="w-5 h-5 text-[#0B1437]" />
                  <span>{t.nav.register}</span>
                </Link>
              </>
            )}
          </div>
        )}
      </header>

      {/* Finestra Modale per Cambio Password */}
      <ChangePasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </>
  );
};

export default Navbar;
